import { useRef, useState } from "react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { Gamepad2, Monitor, Glasses, Disc3, Calendar, Coffee, Users, Smile, Info, Download, Loader2, ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { fmtTime, toDateStr } from "@/lib/slots";

const C = { bgFrom: "#0a1533", bgTo: "#050a18", panel: "#0b1a3d", head: "#102a5e", line: "#1c3a72", cyan: "#38bdf8", cyan2: "#22d3ee", green: "#22c55e", greenTxt: "#4ade80", red: "#ef4444", redTxt: "#f87171", txt: "#dbeafe", sub: "#93c5fd" };

function durLabel(m) {
  const h = Math.floor(m / 60), mm = m % 60;
  if (h && mm) return `${h} Hour${h > 1 ? "s" : ""} ${mm} Min`;
  if (h) return `${h} Hour${h > 1 ? "s" : ""}`;
  return `${mm} Minutes`;
}

const Dot = ({ ok }) => <span style={{ display: "inline-block", width: 13, height: 13, borderRadius: "50%", background: ok ? C.green : C.red, boxShadow: `0 0 8px ${ok ? C.green : C.red}`, marginRight: 8, verticalAlign: "middle" }} />;

function Poster({ data, title, innerRef }) {
  const cols = data.types.length + 2; // VR + Steering
  const th = { padding: "14px 12px", color: C.txt, fontWeight: 800, fontSize: 15, textAlign: "left", letterSpacing: 0.5 };
  const td = { padding: "11px 12px", fontSize: 15, borderTop: `1px solid ${C.line}` };
  const iconWrap = { display: "inline-flex", alignItems: "center", gap: 6, color: C.cyan };
  return (
    <div ref={innerRef} style={{ width: 1080, minHeight: 1350, background: `linear-gradient(160deg, ${C.bgFrom}, ${C.bgTo})`, borderRadius: 22, padding: 26, fontFamily: "'Poppins','Inter',sans-serif", color: C.txt, boxSizing: "border-box" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 62, height: 62, borderRadius: 16, border: `2px solid ${C.cyan}`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 0 18px ${C.cyan}55` }}>
            <Gamepad2 size={34} color={C.cyan} />
          </div>
          <div>
            <div style={{ fontSize: 34, fontWeight: 900, lineHeight: 1, color: "#fff", textShadow: `0 0 14px ${C.cyan}88` }}>PLAY STATION</div>
            <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: 6, color: C.sub }}>GAMING CAFÉ</div>
            <div style={{ fontSize: 12, letterSpacing: 5, color: C.cyan2, marginTop: 3 }}>PLAY • EAT • CONNECT</div>
          </div>
        </div>
        <div style={{ flex: 1, maxWidth: 470, background: C.panel, border: `1px solid ${C.line}`, borderRadius: 16, padding: "14px 20px", textAlign: "center", boxShadow: `0 0 22px ${C.cyan}22` }}>
          <div style={{ fontSize: 26, fontWeight: 900, color: "#fff", lineHeight: 1.15 }}>SLOT AVAILABILITY</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: C.cyan2 }}>{title}</div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 6, color: C.sub, fontWeight: 700 }}>
            <Calendar size={18} color={C.cyan} /> {new Date(data.date + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })} ({data.day_name.slice(0, 3).toUpperCase()})
          </div>
        </div>
        <div style={{ fontStyle: "italic", textAlign: "right", color: C.cyan2, fontSize: 18, fontWeight: 600, lineHeight: 1.3, minWidth: 120 }}>Good Games<br />Better People</div>
      </div>

      {/* Setup summary */}
      <div style={{ display: "flex", alignItems: "center", gap: 26, background: C.panel, border: `1px solid ${C.line}`, borderRadius: 14, padding: "14px 20px", marginBottom: 16 }}>
        <div style={{ color: C.cyan, fontWeight: 900, fontSize: 15, letterSpacing: 1 }}>OUR<br />SETUP</div>
        {data.types.map((t) => (
          <div key={t.type} style={iconWrap}>
            {t.type === "ps5" ? <Gamepad2 size={26} /> : <Monitor size={26} />}
            <span style={{ color: "#fff", fontWeight: 800, fontSize: 17 }}>{t.label}<span style={{ color: C.sub, fontWeight: 600, fontSize: 13 }}> ({t.count} Stations)</span></span>
          </div>
        ))}
        <div style={iconWrap}><Glasses size={26} /><span style={{ color: "#fff", fontWeight: 800, fontSize: 17 }}>VR<span style={{ color: C.sub, fontWeight: 600, fontSize: 13 }}> ({data.vr_count} Unit)</span></span></div>
        <div style={iconWrap}><Disc3 size={26} /><span style={{ color: "#fff", fontWeight: 800, fontSize: 17 }}>STEERING<span style={{ color: C.sub, fontWeight: 600, fontSize: 13 }}> ({data.steering_count} Unit)</span></span></div>
      </div>

      {/* Table */}
      <div style={{ border: `1px solid ${C.line}`, borderRadius: 14, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: C.head }}>
              <th style={th}>TIME SLOT</th>
              <th style={th}>DURATION</th>
              {data.types.map((t) => <th key={t.type} style={th}>{t.label} <span style={{ color: C.sub, fontWeight: 600, fontSize: 12 }}>({t.count} Avl)</span></th>)}
              <th style={th}>VR <span style={{ color: C.sub, fontWeight: 600, fontSize: 12 }}>(1)</span></th>
              <th style={th}>STEERING <span style={{ color: C.sub, fontWeight: 600, fontSize: 12 }}>(1)</span></th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={i} style={{ background: i % 2 ? "#08122b" : "#0a1836" }}>
                <td style={{ ...td, fontWeight: 700, color: "#fff" }}>{fmtTime(r.start)} – {fmtTime(r.end)}</td>
                <td style={{ ...td, color: C.sub }}>{durLabel(r.duration_min)}</td>
                {data.types.map((t) => {
                  const v = r.counts[t.type] || 0;
                  return <td key={t.type} style={{ ...td, color: v > 0 ? C.greenTxt : C.redTxt, fontWeight: 700 }}><Dot ok={v > 0} />{v > 0 ? `${v} Available` : "Booked"}</td>;
                })}
                <td style={{ ...td, color: r.vr_free > 0 ? C.greenTxt : C.redTxt, fontWeight: 700 }}><Dot ok={r.vr_free > 0} />{r.vr_free > 0 ? "Available" : "Booked"}</td>
                <td style={{ ...td, color: r.steering_free > 0 ? C.greenTxt : C.redTxt, fontWeight: 700 }}><Dot ok={r.steering_free > 0} />{r.steering_free > 0 ? "Available" : "Booked"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend / note */}
      <div style={{ display: "flex", gap: 30, alignItems: "flex-start", background: C.panel, border: `1px solid ${C.line}`, borderRadius: 14, padding: "16px 20px", marginTop: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, fontWeight: 700 }}>
          <span><Dot ok={true} /> = Available</span>
          <span><Dot ok={false} /> = Booked</span>
        </div>
        <div style={{ flex: 1, color: C.sub, fontSize: 13, lineHeight: 1.7 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: C.cyan, fontWeight: 800, marginBottom: 4 }}><Info size={18} /> Note:</div>
          • Availability is based on current bookings.<br />
          • Slots can be booked for 30 minutes, 1 hour or more.<br />
          • VR and Steering are add-ons and not blocked by normal bookings.<br />
          • Timings may be subject to change.
        </div>
        <div style={{ fontStyle: "italic", color: C.cyan2, fontSize: 30, fontWeight: 700, alignSelf: "center" }}>Game On!</div>
      </div>

      {/* Footer */}
      <div style={{ display: "flex", justifyContent: "space-around", marginTop: 16, paddingTop: 14, borderTop: `1px solid ${C.line}`, color: C.sub, fontSize: 12, fontWeight: 700 }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}><Coffee size={20} color={C.cyan} /> SNACKS & BEVERAGES</span>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}><Users size={20} color={C.cyan} /> GAMING COMMUNITY</span>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}><Gamepad2 size={20} color={C.cyan} /> PS5 | PC | VR | STEERING</span>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}><Smile size={20} color={C.cyan} /> GOOD GAMES BETTER PEOPLE</span>
      </div>
    </div>
  );
}

export default function SlotPoster() {
  const tomorrow = toDateStr(new Date(Date.now() + 86400000));
  const [date, setDate] = useState(tomorrow);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dl, setDl] = useState(false);
  const ref = useRef(null);

  const titleFor = (d) => {
    const t = toDateStr(new Date());
    const tm = toDateStr(new Date(Date.now() + 86400000));
    if (d === t) return "FOR TODAY";
    if (d === tm) return "FOR TOMORROW";
    return `FOR ${new Date(d + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long" }).toUpperCase()}`;
  };

  const generate = async () => {
    if (!date) return;
    setBusy(true);
    try { const r = await api.get("/admin/availability-grid", { params: { date } }); setData(r.data); setTimeout(() => download(), 700); }
    catch (e) { toast.error("Could not load availability"); } finally { setBusy(false); }
  };

  const download = async () => {
    if (!ref.current) return;
    setDl(true);
    try {
      if (document.fonts && document.fonts.ready) await document.fonts.ready;
      await new Promise((r) => setTimeout(r, 300));
      const dataUrl = await toPng(ref.current, { quality: 1, pixelRatio: 2, backgroundColor: "#050a18", cacheBust: true, width: 1080, skipFonts: true });
      const link = document.createElement("a");
      link.download = `slot-availability-${date}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Poster downloaded (PNG)");
    } catch (e) { toast.error("Download failed"); } finally { setDl(false); }
  };

  return (
    <div className="glass rounded-2xl p-5 mt-6" data-testid="slot-poster-section">
      <div className="flex items-center gap-2 mb-1"><ImageIcon className="text-purple-300" size={22} /><h3 className="font-display font-bold">Availability Poster</h3></div>
      <p className="text-sm text-slate-400 mb-4">Generate a shareable image of available slots for a chosen date — perfect for WhatsApp status & social media.</p>
      <div className="flex flex-wrap gap-3 items-end mb-4">
        <div><label className="text-xs text-purple-400 font-mono">Date</label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="poster-date" className="mt-1 h-10 bg-purple-950/30 border-purple-500/30 [color-scheme:dark]" /></div>
        <Button onClick={generate} data-testid="poster-generate-btn" className="h-10 bg-purple-600 hover:bg-purple-500">{busy ? <Loader2 className="animate-spin" size={16} /> : "Generate & Download PNG"}</Button>
        {data && <Button onClick={download} data-testid="poster-download-btn" className="h-10 bg-emerald-600 hover:bg-emerald-500"><Download size={16} /> {dl ? "Preparing…" : "Download Again"}</Button>}
      </div>

      {data && (
        <div className="overflow-auto rounded-xl border border-purple-500/20 bg-black/30 p-3">
          <div style={{ width: 1080, transform: "scale(0.62)", transformOrigin: "top left", height: "auto" }}>
            <div style={{ display: "inline-block" }}>
              <Poster data={data} title={titleFor(date)} innerRef={ref} />
            </div>
          </div>
          <div style={{ height: 1 }} />
        </div>
      )}
    </div>
  );
}
